import { Component, OnInit, OnDestroy, ViewChild, Input, Output, EventEmitter, effect } from '@angular/core';
import { FindingsSectionComponent } from '../findings-section/findings-section.component';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, FormArray, FormControl, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatStepper } from '@angular/material/stepper';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { FieldInspectionService } from '../../services/field-inspection.service';
import { InspectionPhotoService, PhotoUploadResult } from '../../services/inspection-photo.service';
import { HelperService } from '../../../helper.service';
import { ContextualDataPanelComponent } from '../contextual-data-panel/contextual-data-panel.component';
import { StructuredObservationConfig } from '../../models/inspection-config.model';
import { InspectionPhoto } from '../../models/inspection-photo.model';
import { InspectionFinding } from '../../models/inspection-finding.model';
import { ContextualDataItem } from '../../models/inspection-report.model';
import { ImageViewerDialogComponent } from '../image-viewer-dialog/image-viewer-dialog.component';
import dayjs from 'dayjs';

/** Thin dialog wrapper so the contextual data panel can be opened as a MatDialog */
import { Component as Comp } from '@angular/core';
@Comp({
  selector: 'app-analysis-data-dialog',
  template: `
    <div class="analysis-dialog-header" mat-dialog-title>
      <span>Analysis Data</span>
      <button mat-icon-button [mat-dialog-close]="true" class="analysis-dialog-close">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    <div mat-dialog-content class="analysis-dialog-content">
      <p class="text-xs text-gray-400 mb-3 px-1">Read-only contextual data for this inspection point.</p>
      <app-contextual-data-panel
        [tenantId]="data.tenantId"
        [fieldId]="data.fieldId"
        [lat]="data.lat"
        [lng]="data.lng"
        [capturedData]="data.capturedData">
      </app-contextual-data-panel>
    </div>`,
  styles: [`
    .analysis-dialog-header {
      display: flex; align-items: center; justify-content: space-between;
      padding: 12px 8px 12px 20px; border-bottom: 1px solid #eee; font-weight: 600;
    }
    .analysis-dialog-content { padding: 12px 16px 16px; overflow-y: auto; max-height: 70vh; }
  `],
  standalone: false
})
export class AnalysisDataDialogComponent {
  constructor(@Inject(MAT_DIALOG_DATA) public data: {
    tenantId: number; fieldId: number; lat: number; lng: number;
    capturedData: ContextualDataItem[] | null;
  }) {}
}

@Component({
  selector: 'app-inspection-form',
  templateUrl: './inspection-form.component.html',
  standalone: false
})
export class InspectionFormComponent implements OnInit, OnDestroy {

  // ── Inputs ──────────────────────────────────────────────────────────────
  @Input() pinId: string = '';
  @Input() fieldId: number = 0;
  @Input() tenantId: number = 0;
  @Input() viewOnly: boolean = false;
  @Input() inspectorUserId: number | null = null;
  @Input() cropDefinitionId: number | null = null;
  @Input() seasonId: number | null = null;
  /** Pin coordinates from the dialog opener — the analysis fallback for a report that has no GPS of its own. */
  @Input() lat: number = 0;
  @Input() lng: number = 0;
  @Input() crop: string = '';
  @Input() fieldType: string = '';
  /** When set, the form loads an existing report rather than starting fresh */
  @Input() reportId: string | null = null;

  // ── Outputs ─────────────────────────────────────────────────────────────
  @Output() closeRequested = new EventEmitter<void>();
  @Output() dirtyChange = new EventEmitter<boolean>();
  @Output() formClosed = new EventEmitter<void>();

  // ── ViewChildren ────────────────────────────────────────────────────────
  @ViewChild('stepper') stepper: MatStepper;
  @ViewChild('pestsSection') pestsSection: FindingsSectionComponent;
  @ViewChild('diseasesSection') diseasesSection: FindingsSectionComponent;
  @ViewChild('deficienciesSection') deficienciesSection: FindingsSectionComponent;

  // ── State ────────────────────────────────────────────────────────────────
  form: FormGroup;
  generalPhotos: InspectionPhoto[] = [];
  uploadingPhoto = false;
  savingDraft = false;
  finalisingReport = false;
  readonly MAX_GENERAL_PHOTOS = 5;

  /** Preloaded findings from a loaded report — passed down to each section component */
  preloadedFindings: InspectionFinding[] = [];

  /** GPS coordinates parsed from the loaded report — used for analysis data panel */
  reportLat: number = 0;
  reportLng: number = 0;

  /**
   * Analysis values captured against the loaded report. Stays null for a new
   * inspection, which has no report row yet and so must query live instead.
   */
  reportContextualData: ContextualDataItem[] | null = null;

  /**
   * Stored structuredObservations values from the loaded report, keyed by fieldConfigId.
   * Used to patch the FormArray once config arrives (which may come after the report detail).
   */
  private savedObservationsMap: Map<string, string> = new Map();
  private configLoaded = false;
  private formSub: Subscription;

  // ── Computed ─────────────────────────────────────────────────────────────
  get isInspector(): boolean {
    const roles = JSON.parse(sessionStorage.getItem('roles') || '[]');
    return roles.some((r: any) => r.name === 'Inspector');
  }

  get currentStepIndex(): number {
    return this.stepper?.selectedIndex ?? 0;
  }

  get isLastStep(): boolean {
    return this.currentStepIndex === 4;
  }

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private matDialog: MatDialog,
    public inspectionService: FieldInspectionService,
    private photoService: InspectionPhotoService,
    public helper: HelperService
  ) {
    // React to config signal changes — rebuild FormArray and patch saved values
    effect(() => {
      const config = this.inspectionService.config();
      if (config && this.form) {
        this.buildStructuredObservations(config.structuredObservations);
        this.patchSavedObservations();
        this.configLoaded = true;
      }
    });
  }

  ngOnInit(): void {
    if (!this.pinId) {
      this.pinId = this.route.snapshot.paramMap.get('pinId') ?? '';
    }
    if (!this.viewOnly) {
      this.viewOnly = this.route.snapshot.data['viewOnly'] ?? false;
    }

    if (!this.reportId) {
      const routeReportId = this.route.snapshot.paramMap.get('reportId');
      if (routeReportId) this.reportId = routeReportId;
    }

    this.form = this.fb.group({
      inspectionTimestamp: [dayjs().format('YYYY-MM-DDTHH:mm'), Validators.required],
      generalObservations: [''],
      structuredObservations: this.fb.array([])
    });

    if (this.viewOnly) {
      this.form.disable();
    }

    this.formSub = this.form.valueChanges.subscribe(() => {
      this.dirtyChange.emit(this.form.dirty);
    });

    // For an existing report the authority is the report's own crop/fieldType, loaded
    // below once the detail arrives — it records which config the report was captured
    // against, and is insert-only server-side so it cannot drift from the field's
    // current definitions. Only a new inspection can rely on the inputs, which the
    // dialog resolved from the definition the user just picked.
    if (!this.reportId) {
      this.applyConfigFor(this.crop, this.fieldType);
    }

    // Load existing report — patch scalar fields, hydrate photos and findings
    if (this.reportId) {
      this.inspectionService.loadReportDetail(this.reportId).subscribe({
        next: (report) => {
          this.reportFinalised = report.status === 'FINALISED';

          // Config comes from what this report was captured against, not from whatever
          // the field is configured as now. Falls back to the inputs for reports stored
          // before crop/field_type were recorded.
          this.applyConfigFor(report.crop ?? this.crop, report.fieldType ?? this.fieldType);

          // Patch timestamp and general observations
          if (report.inspectionTimestamp) {
            this.form.patchValue({
              inspectionTimestamp: dayjs(report.inspectionTimestamp).format('YYYY-MM-DDTHH:mm'),
              generalObservations: report.generalObservations ?? ''
            });
          }

          // Cache structured observation values for patching once config is ready
          (report.structuredObservations ?? []).forEach(obs => {
            this.savedObservationsMap.set(obs.fieldConfigId, obs.value ?? '');
          });

          // If config already loaded by the time the report comes back, patch immediately
          if (this.configLoaded) {
            this.patchSavedObservations();
          }

          // Hydrate general photos
          this.generalPhotos = (report.generalPhotos ?? []);

          // Hydrate findings for the section components
          this.preloadedFindings = (report.findings ?? []);

          // Default to [] rather than null: a saved report shows what was captured for it,
          // and reports created before capture existed correctly show nothing.
          this.reportContextualData = report.contextualData ?? [];

          // Capture fieldId from report if not provided as input (e.g. opened from tenant tab)
          if (!this.fieldId && report.fieldId) {
            this.fieldId = report.fieldId;
          }

          // Parse GPS coordinates for analysis data panel
          if (report.gpsCoordinates) {
            const parts = report.gpsCoordinates.split(',');
            if (parts.length >= 2) {
              const lat = parseFloat(parts[0].trim());
              const lng = parseFloat(parts[1].trim());
              if (!isNaN(lat) && !isNaN(lng)) {
                this.reportLat = lat;
                this.reportLng = lng;
              }
            }
          }
        },
        error: () => {}
      });
    }
  }

  ngOnDestroy(): void {
    this.formSub?.unsubscribe();
  }

  // ── Structured observations ───────────────────────────────────────────────
  buildStructuredObservations(configs: StructuredObservationConfig[]): void {
    const array = this.form.get('structuredObservations') as FormArray;
    array.clear();
    configs.forEach(config => {
      array.push(this.fb.group({
        fieldConfigId: [config.id],
        value: [{ value: '', disabled: this.viewOnly }]
      }));
    });
  }

  /**
   * Patches saved values into the FormArray controls by matching fieldConfigId.
   * Safe to call multiple times — only patches if there are saved values to apply.
   */
  private patchSavedObservations(): void {
    if (this.savedObservationsMap.size === 0) return;
    const array = this.form.get('structuredObservations') as FormArray;
    array.controls.forEach(group => {
      const configId = group.get('fieldConfigId')?.value as string;
      if (configId && this.savedObservationsMap.has(configId)) {
        group.get('value')?.setValue(this.savedObservationsMap.get(configId), { emitEvent: false });
      }
    });
  }

  get structuredObservationsArray(): FormArray {
    return this.form.get('structuredObservations') as FormArray;
  }

  getStructuredControl(index: number): FormControl {
    return this.structuredObservationsArray.at(index).get('value') as FormControl;
  }

  // ── Photo upload ─────────────────────────────────────────────────────────
  onPhotoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    if (this.generalPhotos.length >= this.MAX_GENERAL_PHOTOS) {
      this.snackBar.open(`Maximum ${this.MAX_GENERAL_PHOTOS} photos allowed`, 'Dismiss', { duration: 3000 });
      return;
    }

    const file = input.files[0];
    this.uploadingPhoto = true;
    this.photoService.uploadPhoto(
      file,
      this.helper.getTenantId(),
      this.reportId ?? '',
      'general'
    ).subscribe({
      next: (result: PhotoUploadResult) => {
        this.generalPhotos.push({
          section: 'GENERAL',
          firebaseStoragePath: result.firebaseStoragePath,
          downloadUrl: result.downloadUrl
        });
        this.uploadingPhoto = false;
        input.value = '';
        this.markOutOfBandChange();
      },
      error: () => {
        this.snackBar.open('Photo upload failed. Please try again.', 'Dismiss', { duration: 3000 });
        this.uploadingPhoto = false;
      }
    });
  }

  removePhoto(index: number): void {
    this.generalPhotos.splice(index, 1);
    this.markOutOfBandChange();
  }

  /**
   * Photos and findings are held outside the FormGroup, so changing them leaves the form
   * pristine and any save gated on form.dirty — auto-save on step navigation — skips
   * without a word. Mark the form itself dirty so those changes are treated as edits.
   */
  private markOutOfBandChange(): void {
    this.form.markAsDirty();
    this.dirtyChange.emit(true);
  }

  onFindingsChanged(): void {
    this.markOutOfBandChange();
  }

  // ── Navigation ───────────────────────────────────────────────────────────
  next(): void {
    this.autoSaveOnNavigate();
    this.stepper?.next();
  }

  back(): void {
    this.autoSaveOnNavigate();
    this.stepper?.previous();
  }

  onCloseClick(): void {
    this.closeRequested.emit();
  }

  /**
   * Loads the dynamic form config for a crop and field type. Both are required by the
   * config endpoint, so without them there is nothing to ask for — set an empty config
   * rather than leaving the sections on a permanent "Loading..." state.
   */
  private applyConfigFor(crop: string | null | undefined, fieldType: string | null | undefined): void {
    if (crop && fieldType) {
      this.inspectionService.loadConfig(crop, fieldType);
    } else {
      this.inspectionService.config.set({
        structuredObservations: [], pests: [], diseases: [], deficiencies: []
      });
    }
  }

  // ── Image viewer ─────────────────────────────────────────────────────────
  openImage(photo: InspectionPhoto): void {
    if (!photo?.downloadUrl) return;
    this.matDialog.open(ImageViewerDialogComponent, {
      data: { url: photo.downloadUrl, caption: 'General observations' },
      panelClass: 'fi-image-viewer-panel',
      maxWidth: '95vw',
      autoFocus: false
    });
  }

  // ── Re-send PDF ──────────────────────────────────────────────────────────
  /** In flight — held disabled because the email fans out to every Inspection Viewer. */
  retriggeringPdf = false;

  /**
   * Whether the open report is finalised. Re-send keys off this rather than viewOnly: a draft
   * also opens read-only for a requestor or viewer, and a draft has no PDF to send.
   */
  reportFinalised = false;

  retriggerPdf(): void {
    if (!this.reportId || this.retriggeringPdf) return;

    this.retriggeringPdf = true;
    this.inspectionService.retriggerPdf(this.reportId).subscribe({
      next: (res) => {
        this.retriggeringPdf = false;
        // Asynchronous generation — this confirms queuing, not delivery.
        this.snackBar.open(
          res?.message ?? 'PDF generation retriggered. It will be emailed once generated.',
          'Dismiss',
          { duration: 5000 }
        );
      },
      error: () => {
        this.retriggeringPdf = false;
        this.snackBar.open('Could not re-send the report. Please try again.', 'Dismiss', { duration: 4000 });
      }
    });
  }

  // ── Analysis panel ───────────────────────────────────────────────────────
  openAnalysisPanel(): void {
    this.matDialog.open(AnalysisDataDialogComponent, {
      data: {
        tenantId: this.helper.getTenantId(),
        fieldId: this.fieldId,
        // Report GPS when we have it, otherwise the pin we were opened on — a new
        // inspection has no report to take coordinates from.
        lat: this.reportLat || this.lat,
        lng: this.reportLng || this.lng,
        capturedData: this.reportContextualData
      },
      width: '600px',
      maxWidth: '95vw',
      maxHeight: '85vh',
      autoFocus: false
    });
  }

  // ── Save draft ───────────────────────────────────────────────────────────
  /**
   * True once the findings sections actually exist in the view. They sit behind
   * *ngIf="inspectionService.config()", so before config arrives — or if the config
   * request failed — getFindings() returns nothing for every section, which is
   * indistinguishable from "the user deselected everything".
   */
  private get findingsSectionsReady(): boolean {
    return !!(this.pestsSection && this.diseasesSection && this.deficienciesSection);
  }

  saveDraft(silent = false): void {
    if (this.form.invalid || this.savingDraft) return;
    this.savingDraft = true;

    const formValue = this.form.getRawValue();

    // Both or neither. The API only derives crop and field type when it receives neither,
    // so sending one alone suppresses that fallback and leaves the other permanently null.
    // Either is empty when the season meta lookup failed to resolve a real description.
    const definitionKnown = !!(this.crop && this.fieldType);

    const payload: any = {
      pinId: +this.pinId,
      fieldId: this.fieldId || 0,
      tenantId: this.tenantId || this.helper.getTenantId(),
      inspectorUserId: this.inspectorUserId ?? this.helper.getUserId(),
      cropDefinitionId: this.cropDefinitionId ?? null,
      seasonId: this.seasonId ?? null,
      crop: definitionKnown ? this.crop : null,
      fieldType: definitionKnown ? this.fieldType : null,
      status: 'DRAFT' as const,
      // The datetime-local control yields a zoneless local wall clock ("2026-08-11T13:32").
      // inspection_timestamp is TIMESTAMPTZ, so sending that as-is makes the server read it
      // in its own zone (UTC) and store SAST wall-clock as a UTC instant — the value then
      // reads back two hours ahead. Convert to a real instant so the offset survives.
      inspectionTimestamp: dayjs(formValue.inspectionTimestamp).toISOString(),
      generalObservations: formValue.generalObservations,
      generalPhotos: this.generalPhotos
    };

    // updateReport deletes and re-inserts these collections whenever the key is present,
    // so an empty array is a delete-everything instruction. Send them only when the view
    // can actually speak for them; omitting the key leaves what is already stored alone.
    if (this.findingsSectionsReady) {
      payload.findings = [
        ...(this.pestsSection?.getFindings() ?? []),
        ...(this.diseasesSection?.getFindings() ?? []),
        ...(this.deficienciesSection?.getFindings() ?? [])
      ];
    }
    if (this.inspectionService.config()) {
      payload.structuredObservations = formValue.structuredObservations;
    }

    const request$ = this.reportId
      ? this.inspectionService.updateReport(this.reportId, payload)
      : this.inspectionService.createReport(payload);

    request$.subscribe({
      next: (saved) => {
        this.reportId = saved.id ?? null;
        this.form.markAsPristine();
        this.dirtyChange.emit(false);
        if (!silent) {
          this.snackBar.open('Draft saved successfully', 'Dismiss', { duration: 3000 });
        }
        this.savingDraft = false;
      },
      error: () => {
        // Surfaced even on an auto-save — the user is walking away from unsaved work.
        this.snackBar.open('Failed to save draft. Please try again.', 'Dismiss', { duration: 3000 });
        this.savingDraft = false;
      }
    });
  }

  /**
   * Auto-save on step navigation. Never blocks the move: an invalid or untouched form
   * simply navigates without saving, matching the Save Draft button, which is disabled
   * under the same conditions.
   */
  private autoSaveOnNavigate(): void {
    if (this.viewOnly) return;
    if (this.form.invalid || !this.form.dirty) return;
    if (this.savingDraft || this.uploadingPhoto) return;
    if (!this.inspectionService.config()) return;
    this.saveDraft(true);
  }

  // ── Finalise ─────────────────────────────────────────────────────────────
  finalise(): void {
    if (!this.reportId) {
      this.snackBar.open('Please save a draft before finalising.', 'Dismiss', { duration: 3000 });
      return;
    }
    if (this.finalisingReport) return;
    this.finalisingReport = true;

    this.inspectionService.finaliseReport(this.reportId).subscribe({
      next: () => {
        this.viewOnly = true;
        this.reportFinalised = true;
        this.form.disable();
        this.dirtyChange.emit(false);
        this.snackBar.open(
          'Report finalised. The PDF will be emailed to Inspection Viewers.',
          'Dismiss',
          { duration: 5000 }
        );
        this.finalisingReport = false;
      },
      error: () => {
        this.snackBar.open('Failed to finalise report. Please try again.', 'Dismiss', { duration: 3000 });
        this.finalisingReport = false;
      }
    });
  }
}
